/**
 * Estimate math — the single source of truth for MoveFlow pricing.
 *
 * These functions are PURE (no I/O, no side effects) so they can be unit-tested
 * and reused by later phases (invoices, contractor estimates). The UI persists
 * line items / discounts through the API and then recomputes here so the numbers
 * on screen are always derived from the same code the backend caches with.
 *
 * Order of operations (must match the legacy CRM to the cent):
 *   1. Base line gross from the tariff, floored at minimum_charge (not on an
 *      hourly tariff: its floor is minimum hours), then the
 *      lead's base discount is applied.
 *   2. Each fee/custom line gross, then that line's per-line discount.
 *   3. subtotal = sum of all line nets.
 *   4. total discount applied to subtotal -> discountedSubtotal.
 *   5. tax = discountedSubtotal * (branch.tax_rate / 100).
 *   6. total = discountedSubtotal + tax.
 */

export type DiscountType = 'flat' | 'pct' | null | undefined;
export type LineSource = 'base' | 'fee' | 'custom';

export interface EstimateLead {
  weight_lbs?: number | string | null;
  minimum_weight?: number | string | null; // customer billing floor (MW); default 2000
  contractor_minimum_weight?: number | string | null; // contractor-pay floor (MW2); mirrors client until overridden
  cubic_feet?: number | string | null;
  base_discount_type?: DiscountType;
  base_discount_value?: number | string | null;
  total_discount_type?: DiscountType;
  total_discount_value?: number | string | null;
}

export interface EstimateTariff {
  name?: string | null;
  pricing_method?: 'weight' | 'cuft' | 'flat' | 'hourly' | null;
  base_rate?: number | string | null;
  /** Never applied to an HOURLY tariff: local jobs have minimum hours instead (§9.0.7). */
  minimum_charge?: number | string | null;
}

export interface EstimateBranch {
  name?: string | null;
  tax_rate?: number | string | null;
}

export interface EstimateFee {
  id: string;
  name?: string | null;
  fee_type?: 'flat' | 'pct' | 'unit' | null;
  amount?: number | string | null;
  percentage?: number | string | null;
  rate?: number | string | null;
  unit_label?: string | null;
  applied_to?: 'base' | 'subtotal' | null;
  /** A1: pct floor — "14% or $300, whichever is higher". Absent on ordinary fees. */
  minimum_amount?: number | string | null;
  /** A1: per-unit allowance charged at zero — 1 free flight, the first 75 ft. */
  free_units?: number | string | null;
  /** Fee catalog: false = hidden from "Add a fee". */
  active?: boolean | null;
}

/**
 * A1 — an accessorial rate row (accessorial_rates), per tariff / payer / leg.
 * Priced by the SAME engine as any other line: it is mapped to a fee-like shape
 * and run through calcFeeAmt, so there is no second pricing path.
 */
export interface AccessorialRate {
  id: string;
  tariff_id?: string | null;
  payer?: 'client' | 'contractor' | string | null;
  accessorial: string;                 // 'elevator' | 'stairs' | 'long_walk' | 'shuttle'
  leg: string;                         // 'origin' | 'destination'
  label?: string | null;
  pricing_method?: 'flat' | 'unit' | 'pct' | null;
  amount?: number | string | null;
  rate?: number | string | null;
  unit_label?: string | null;
  free_units?: number | string | null;
  percentage?: number | string | null;
  minimum_amount?: number | string | null;
  active?: boolean | null;
}

/** The key that ties an auto-added line to the rate row that produced it. */
export function accessorialKey(leg: string, accessorial: string): string {
  return `${leg}:${accessorial}`;
}

/**
 * An accessorial rate as a fee, so calcFeeAmt prices it. applied_to is ALWAYS
 * 'base': an accessorial percentage is a percentage of the transportation
 * charge, never of the subtotal. That is not configurable, by design.
 */
export function feeFromAccessorial(r: AccessorialRate): EstimateFee {
  const method = r.pricing_method ?? 'flat';
  return {
    id: r.id,
    name: r.label ?? r.accessorial,
    fee_type: method === 'unit' ? 'unit' : method === 'pct' ? 'pct' : 'flat',
    amount: r.amount,
    rate: r.rate,
    percentage: r.percentage,
    unit_label: r.unit_label,
    applied_to: 'base',
    minimum_amount: r.minimum_amount,
    free_units: r.free_units,
  };
}

export interface EstimateItemRow {
  id?: string | null;
  fee_id?: string | null;
  description?: string | null;
  amount?: number | string | null;
  quantity?: number | string | null;
  discount_type?: DiscountType;
  discount_value?: number | string | null;
  rate_snapshot?: number | string | null;
  source?: LineSource | null;
  sort_order?: number | string | null;
  /** A1: '{leg}:{accessorial}' when this line was auto-added; null if a human typed it. */
  auto_key?: string | null;
  /** A1: true once a rep changed the line — from then on nothing recomputes it. */
  auto_edited?: boolean | null;
  /**
   * 051: a rep picked this access fee from "Add a fee". It carries auto_key, so
   * it prices from the rate row exactly as above; only the access sync reads it.
   */
  added_manually?: boolean | null;
  /** Hourly base line (048): billed hours. Null = legacy, quantity 1 — totals unchanged. */
  hours?: number | string | null;
  /** Hourly base line (048): crew shown on the line. Display only; the rate is per crew-hour. */
  crew_size?: number | string | null;
  /** Hourly base line (053): trucks on the job. Display only; extra trucks are a fee line. */
  trucks?: number | string | null;
  /** Hourly base line (053): the job's minimum. Billed hours = max(hours, minimum_hours). */
  minimum_hours?: number | string | null;
  /**
   * Hourly base line (054): the travel part of `hours`, entered apart from the
   * on-site estimate in the editor. Pricing never reads it: `hours` is the total.
   */
  travel_hours?: number | string | null;
  /**
   * Fee line (053): a rep edited the rate on the estimate, so rate_snapshot IS
   * the rate ($ flat, $/unit, or %). False = the fee's own rate, as always —
   * rate_snapshot on a fee line was otherwise only a copy taken when it was added.
   */
  rate_overridden?: boolean | null;
  /** Finalize: a draft adjustment riding along as a custom line. */
  adjustment?: boolean | null;
  /**
   * Finalize (055): a fee added on the draft carries its own fee row — the
   * CLIENT values snapshotted when it was added — and is priced from it rather
   * than from the catalog, so a later catalog change cannot reprice the bill.
   */
  fee_row?: EstimateFee | null;
  /** Finalize (055): the draft fee this line came from. */
  draft_fee_id?: string | null;
}

export interface CalcLine {
  id: string | null;
  desc: string;
  gross: number;
  discountAmt: number;
  net: number;
  source: LineSource;
  feeId: string | null;
  quantity: number;
  rate: number;
  /** Presentational hint for the Rate column: '$', '$/unit', '%'. */
  rateKind: 'money' | 'unit' | 'pct';
  unitLabel: string | null;
  discountType: DiscountType;
  discountValue: number;
  /** A1: set when this line was auto-added from an access condition. */
  autoKey: string | null;
  /** A1: pct floor, so the sub-line can read "14% or $300, whichever is higher". */
  minimumAmount: number;
  /** A1: units charged at zero; `quantity` is already net of this. */
  freeUnits: number;
  /** A1: the units recorded on the lead, before the free allowance. */
  rawQuantity: number;
  /** Hourly base line: billed hours, when recorded (048). */
  hours?: number | null;
  /** Hourly base line: crew size, when recorded (048). */
  crewSize?: number | null;
  /** Hourly base line: estimated hours (travel included) as entered — before rounding up and the minimum (053). */
  estimatedHours?: number | null;
  /** Hourly base line: the job's minimum hours, when recorded (053). */
  minimumHours?: number | null;
  /** Hourly base line: trucks, when recorded (053). */
  trucks?: number | null;
  /** Fee line: the rate was edited on the estimate (053). */
  rateOverridden?: boolean;
  /** Finalize: this line is a draft adjustment, not an estimate line. */
  adjustment?: boolean;
  /** Finalize (055): this line is a fee added on the draft — its draft fee id. */
  draftFeeId?: string;
}

export interface CalcEstimate {
  items: CalcLine[];
  subtotal: number;
  totalDiscountAmt: number;
  discountedSubtotal: number;
  tax: number;
  taxRate: number;
  total: number;
  billableWeight: number; // max(weight_lbs, minimum_weight) — floored display weight (MW)
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const present = (v: unknown): boolean => v != null && v !== '';

/**
 * Apply a discount to a gross amount. `flat` subtracts the raw value; `pct`
 * subtracts value% of gross. The discount is clamped to [0, gross] so a line
 * can never go negative.
 */
export function applyDiscount(
  gross: number,
  type: DiscountType,
  value: number | string | null | undefined
): { gross: number; discountAmt: number; net: number } {
  let amt = 0;
  if (type === 'flat') amt = num(value);
  else if (type === 'pct') amt = (gross * num(value)) / 100;
  amt = Math.min(amt, gross);
  if (amt < 0) amt = 0;
  return { gross, discountAmt: amt, net: gross - amt };
}

/**
 * Gross amount for a fee line before its per-line discount.
 *   flat: amount * qty
 *   unit: rate * qty
 *   pct:  percentage% of (base gross, or the running subtotal when applied_to='subtotal')
 */
/**
 * A1 — the units actually charged on a per-unit line: everything above the free
 * allowance. 3 flights with 1 free charges 2. Ordinary fees carry no allowance,
 * so free_units is 0 and this is the identity.
 */
export function chargeableUnits(fee: EstimateFee, qty: number): number {
  return Math.max(0, num(qty) - num(fee.free_units));
}

export function calcFeeAmt(
  fee: EstimateFee,
  transportationCharge: number,
  subtotalSoFar: number,
  qty: number
): number {
  switch (fee.fee_type) {
    case 'flat':
      return num(fee.amount) * qty;
    case 'unit':
      return num(fee.rate) * chargeableUnits(fee, qty);
    case 'pct': {
      // A1: `transportationCharge` is the DISCOUNTED base. A line discount on
      // transportation means the agreed rate IS the discounted one — $1.00/lb
      // less 5% is $0.95/lb — so a percentage computes off what is actually
      // being charged, not off a rate nobody is paying. A SUBTOTAL-scoped
      // percentage is unchanged: subtotalSoFar was already net of discounts.
      const base = fee.applied_to === 'subtotal' ? subtotalSoFar : transportationCharge;
      const pct = (num(fee.percentage) / 100) * base;
      // "14% or $300, whichever is higher" — the floor E4b flagged as missing.
      return Math.max(pct, num(fee.minimum_amount));
    }
    default:
      return 0;
  }
}

/**
 * Gross for a simple rate × quantity line — the shared arithmetic behind a base
 * line (weight/cuft/hourly), a per-unit fee, and a custom line. Factored out so
 * both the customer engine (calcEstimate) and the contractor engine
 * (calcContractorEstimate) compute lines with the same code.
 */
export function calcRateLine(
  rate: number | string | null | undefined,
  quantity: number | string | null | undefined
): number {
  return num(rate) * num(quantity);
}

/** Human-readable line description for a fee at a given quantity. */
export function buildFeeDesc(fee: EstimateFee, qty: number): string {
  const name = fee.name ?? 'Fee';
  // NAME ONLY. This used to bake the arithmetic into the label — "Stair Fee
  // (3 flights × $25.00)" — which is a SECOND way of stating a rule that the
  // sub-line already states, in a different format, on the same row. One rule
  // format, rendered once, in the sub-line.
  if (fee.fee_type === 'unit') {
    return name;
  }
  if (fee.fee_type === 'flat' && qty > 1) {
    return name;
  }
  if (fee.fee_type === 'pct') {
    return `${name} (${num(fee.percentage)}% of ${fee.applied_to === 'subtotal' ? 'subtotal' : 'base'})`;
  }
  return name;
}

/**
 * A fee at the rate a rep typed on the estimate (053): $ for flat, $/unit for
 * per unit, % for a percentage. Everything else about the fee — minimum, free
 * allowance, what a percentage applies to — is unchanged.
 */
/**
 * Estimated hours, billed in 15-minute steps, ROUNDED UP: 5 h 05 → 5.25,
 * 5 h 00 → 5.00, 4 h 46 → 5.00. Hours are stored at whole-minute precision
 * (5 h 05 is 5.0833…), so the minutes are taken to a millionth first: float
 * noise like 304.99999999999997 is 305, and an exact quarter never rounds up.
 */
export function roundUpToQuarterHour(hours: number): number {
  const minutes = Math.round(hours * 60 * 1e6) / 1e6;
  return Math.ceil(minutes / 15 - 1e-9) / 4;
}

export function withRateOverride(fee: EstimateFee, rate: number | string | null | undefined): EstimateFee {
  const r = num(rate);
  if (fee.fee_type === 'unit') return { ...fee, rate: r };
  if (fee.fee_type === 'pct') return { ...fee, percentage: r };
  return { ...fee, amount: r };
}

/** Effective base $/unit rate: a stored base line's snapshot overrides the tariff. */
function effectiveBaseRate(baseItem: EstimateItemRow | undefined, tariff?: EstimateTariff | null): number {
  if (baseItem && baseItem.rate_snapshot != null && baseItem.rate_snapshot !== '') {
    return num(baseItem.rate_snapshot);
  }
  return num(tariff?.base_rate);
}

/** The floored weight used for BOTH billing and display (MW). This is the one
 * place the minimum-weight floor lives: displayed = stored = billed weight. */
export function billableWeight(lead: EstimateLead): number {
  return Math.max(num(lead.weight_lbs), num(lead.minimum_weight));
}

/** Units the base rate multiplies against (weight/cuft), or 1 for flat/hourly. */
function baseUnits(lead: EstimateLead, tariff?: EstimateTariff | null): number {
  switch (tariff?.pricing_method) {
    case 'weight':
      return billableWeight(lead); // floor: never bill below the minimum weight
    case 'cuft':
      return num(lead.cubic_feet);
    default:
      return 1; // flat / hourly
  }
}

/**
 * Compute the full estimate from a lead, its line items, and the referenced
 * tariff / branch / fee catalog. See the file header for the order of operations.
 */
/**
 * What a CLIENT sees for the transportation line. Keyed on the pricing method,
 * not the tariff name — an hourly job is labour, everything else is transport.
 */
export function baseChargeLabel(tariff: EstimateTariff | null | undefined): string {
  return tariff?.pricing_method === 'hourly' ? 'Labour charge' : 'Transportation charge';
}

export function calcEstimate(
  lead: EstimateLead,
  lineItems: EstimateItemRow[],
  tariff: EstimateTariff | null | undefined,
  branch: EstimateBranch | null | undefined,
  fees: EstimateFee[],
  // A1: the tariff's CLIENT accessorial rates. Defaults to [] so a caller that
  // has none behaves exactly as before.
  accessorials: AccessorialRate[] = [],
  // Estimated hours round UP to the next 15 minutes (see roundUpToQuarterHour).
  // calcFinal turns it off: Finalize bills its final hours as entered, as it
  // always has — whether it should round too is Phase 2's decision.
  opts: { roundHoursUp?: boolean } = {}
): CalcEstimate {
  const items: CalcLine[] = [];

  // ---- 1. Base line -------------------------------------------------------
  const baseItem = lineItems.find((li) => li.source === 'base');
  const baseRate = effectiveBaseRate(baseItem, tariff);
  const units = baseUnits(lead, tariff);
  const method = tariff?.pricing_method;

  // weight/cuft multiply by units; flat/hourly are a single unit (qty 1).
  // Hourly: the base line's recorded hours (048), rounded UP to the next 15
  // minutes, never below its minimum (053): 5 h 05 estimated bills 5.25; 3 with
  // a 4-hour minimum bills 4. Without hours — every estimate
  // written before 048 — the quantity stays 1, so no existing total moves.
  const estHours = method === 'hourly' && baseItem && present(baseItem.hours) && num(baseItem.hours) > 0 ? num(baseItem.hours) : null;
  const minHours = method === 'hourly' && baseItem && present(baseItem.minimum_hours) && num(baseItem.minimum_hours) > 0 ? num(baseItem.minimum_hours) : null;
  const roundedHours = estHours != null && opts.roundHoursUp !== false ? roundUpToQuarterHour(estHours) : estHours;
  const baseHours = roundedHours != null ? Math.max(roundedHours, minHours ?? 0) : null;
  const baseTrucks = method === 'hourly' && baseItem && present(baseItem.trucks) && num(baseItem.trucks) > 0 ? num(baseItem.trucks) : null;
  const baseCrew = baseItem && present(baseItem.crew_size) && num(baseItem.crew_size) > 0 ? num(baseItem.crew_size) : null;
  const baseQty = method === 'weight' || method === 'cuft' ? units : baseHours ?? 1;
  let baseGross = calcRateLine(baseRate, baseQty);
  // The tariff's minimum CHARGE floors weight / cuft / flat bases only. An
  // hourly job's floor is its minimum hours (above), never a dollar minimum.
  if (method !== 'hourly') baseGross = Math.max(baseGross, num(tariff?.minimum_charge));

  const baseDisc = applyDiscount(baseGross, lead.base_discount_type, lead.base_discount_value);
  items.push({
    id: baseItem?.id ?? null,
    // CLIENT-FACING label, never the tariff's internal name. A tariff is called
    // things like "By Weight" or "LD Standard" — operational shorthand that read
    // as the charge description on invoices and estimates until this was fixed.
    // The tariff name stays available to staff via calc.tariffName.
    desc: baseChargeLabel(tariff),
    gross: baseDisc.gross,
    discountAmt: baseDisc.discountAmt,
    net: baseDisc.net,
    source: 'base',
    feeId: null,
    quantity: baseQty,
    rate: baseRate,
    // 'unit' when the charge is per lb / per cuft, so the sub-line states the
    // rule the way the contractor documents already do.
    rateKind: method === 'weight' || method === 'cuft' || baseHours != null ? 'unit' : 'money',
    unitLabel: method === 'weight' ? 'lb' : method === 'cuft' ? 'cuft' : baseHours != null ? 'hr' : null,
    discountType: lead.base_discount_type,
    discountValue: num(lead.base_discount_value),
    autoKey: null,
    minimumAmount: 0,
    freeUnits: 0,
    rawQuantity: baseQty,
    hours: baseHours,
    crewSize: method === 'hourly' ? baseCrew : null,
    estimatedHours: estHours,
    minimumHours: minHours,
    trucks: baseTrucks,
  });

  // ---- 2. Fee / custom lines ---------------------------------------------
  const otherItems = lineItems
    .filter((li) => li.source !== 'base')
    .sort((a, b) => num(a.sort_order) - num(b.sort_order));

  let subtotalSoFar = baseDisc.net;

  for (const li of otherItems) {
    const qty = li.quantity != null && li.quantity !== '' ? num(li.quantity) : 1;
    // A1 — an AUTO-ADDED accessorial line prices from its rate row, so changing a
    // rate in Settings reprices every estimate that still owns its auto line.
    // Once a rep edits the line (auto_edited) it is theirs: it falls through to
    // the ordinary stored-amount path and nothing recomputes it.
    const autoRate =
      li.auto_key && !li.auto_edited
        ? accessorials.find((r) => accessorialKey(r.leg, r.accessorial) === li.auto_key && r.active !== false)
        : undefined;
    // An auto line whose RATE a rep edited (053) keeps pricing from its rate row
    // — percentage, minimum and free allowance intact — at the edited rate.
    const autoRateOverridden =
      !autoRate && li.auto_key && li.auto_edited && li.rate_overridden
        ? accessorials.find((r) => accessorialKey(r.leg, r.accessorial) === li.auto_key && r.active !== false)
        : undefined;
    const rowFee = autoRate ?? autoRateOverridden;
    const baseFee = rowFee ? feeFromAccessorial(rowFee) : li.fee_row ? li.fee_row : li.fee_id ? fees.find((f) => f.id === li.fee_id) : undefined;
    const fee = baseFee && li.rate_overridden ? withRateOverride(baseFee, li.rate_snapshot) : baseFee;

    let gross: number;
    let desc: string;
    let rate: number;
    let rateKind: CalcLine['rateKind'] = 'money';
    let unitLabel: string | null = null;

    if (fee) {
      // baseDisc.net, not .gross — percentages compute on the DISCOUNTED
      // transportation charge (A1).
      gross = calcFeeAmt(fee, baseDisc.net, subtotalSoFar, qty);
      desc = rowFee ? (rowFee.label || li.description || '') : li.description || buildFeeDesc(fee, qty);
      if (fee.fee_type === 'unit') {
        // The fee's rate — what the amount is computed from — unless edited (053).
        rate = num(fee.rate);
        rateKind = 'unit';
        unitLabel = fee.unit_label ?? null;
      } else if (fee.fee_type === 'pct') {
        rate = num(fee.percentage);
        rateKind = 'pct';
      } else {
        rate = num(fee.amount);
        rateKind = 'money';
      }
    } else {
      // Custom line (no fee): rate snapshot × qty.
      rate = li.rate_snapshot != null && li.rate_snapshot !== '' ? num(li.rate_snapshot) : num(li.amount);
      gross = calcRateLine(rate, qty);
      desc = li.description || 'Custom line';
      rateKind = 'money';
    }

    const disc = applyDiscount(gross, li.discount_type, li.discount_value);
    items.push({
      id: li.id ?? null,
      desc,
      gross: disc.gross,
      discountAmt: disc.discountAmt,
      net: disc.net,
      source: (li.source as LineSource) ?? 'fee',
      feeId: li.fee_id ?? null,
      // A1: the BILLED quantity — net of any free allowance — so the sub-line
      // states what is actually charged ("3 flights, 1 free" reads as x 2).
      quantity: fee && fee.fee_type === 'unit' ? chargeableUnits(fee, qty) : qty,
      rate,
      rateKind,
      unitLabel,
      discountType: li.discount_type,
      discountValue: num(li.discount_value),
      autoKey: li.auto_key ?? null,
      minimumAmount: fee ? num(fee.minimum_amount) : 0,
      freeUnits: fee ? num(fee.free_units) : 0,
      rawQuantity: qty,
      ...(fee && li.rate_overridden ? { rateOverridden: true } : {}),
      ...(li.adjustment ? { adjustment: true } : {}),
      ...(li.draft_fee_id ? { draftFeeId: li.draft_fee_id } : {}),
    });

    subtotalSoFar += disc.net;
  }

  // ---- 3-6. Subtotal, total discount, tax, total -------------------------
  const subtotal = items.reduce((s, it) => s + it.net, 0);
  const totalDisc = applyDiscount(subtotal, lead.total_discount_type, lead.total_discount_value);
  const discountedSubtotal = totalDisc.net;
  const taxRate = num(branch?.tax_rate);
  const tax = discountedSubtotal * (taxRate / 100);
  const total = discountedSubtotal + tax;

  return {
    items,
    subtotal,
    totalDiscountAmt: totalDisc.discountAmt,
    discountedSubtotal,
    tax,
    taxRate,
    total,
    billableWeight: billableWeight(lead),
  };
}

// ============================================================================
// FINALIZE (Phase 1) — the invoice a job is billed on, from a draft.
// ----------------------------------------------------------------------------
// BUILT ON calcEstimate, so there is still one pricing path. A draft carries:
//   - the estimate inputs FROZEN when it opened (lead, lines, tariff, branch,
//     fees, accessorials) — a tariff edited later cannot reprice the bill, and
//     the estimate itself is never changed;
//   - the final weight (long distance) or final hours / crew (local hourly);
//   - lineRates: per-line rate overrides (the DVP premium repriced at the final
//     weight is one);
//   - adjustments, each taxable or not; and an optional tip.
//
// Order of operations:
//   1. calcEstimate on the frozen inputs with the final weight / hours applied.
//      Every rate is the estimate's: $0.79/lb at 3,000 lb is $0.79/lb at
//      10,000 lb. Percentage fees recompute on the final base, as they would on
//      any base. The minimum weight and minimum charge still apply (the
//      minimum charge never on an hourly tariff). Local hourly bills
//      finalBilledHours: travel + on-site rounded up to 15 min, never below
//      the minimum, or the rep's override; Additional Truck follows trucks.
//   2. TAXABLE adjustments ride along as custom lines at the end, so they join
//      the subtotal before the total discount and the tax.
//   3. NON-TAXABLE adjustments are added after tax.
//   4. The tip last, never taxed: a % of the pre-tax subtotal (discounted
//      subtotal plus non-taxable adjustments), or a flat amount. Rounded to the
//      cent here, because it is a figure the client sees on its own line.
// ============================================================================

export interface FinalInputs {
  lead: EstimateLead;
  items: EstimateItemRow[];
  tariff: EstimateTariff | null | undefined;
  branch: EstimateBranch | null | undefined;
  fees: EstimateFee[];
  accessorials?: AccessorialRate[];
}

/**
 * A catalog fee added on the Finalize draft (055, invoice_drafts.fees). The
 * fee's CLIENT values are copied in when it is added; no contractor figure is
 * ever stored here (the draft is staff-visible).
 */
export interface FinalDraftFee {
  id: string;
  /** The catalog fee — null for an access fee (062: access_key instead). */
  fee_id: string | null;
  /**
   * An access fee added on the draft (062): '<leg>:<accessorial>' of the
   * tariff's CLIENT accessorial rate it was priced from ('origin:long_walk').
   * Its values below are that rate row's, copied when it was added.
   */
  access_key?: string | null;
  name?: string | null;
  fee_type?: 'flat' | 'pct' | 'unit' | null;
  amount?: number | string | null;
  rate?: number | string | null;
  percentage?: number | string | null;
  unit_label?: string | null;
  applied_to?: 'base' | 'subtotal' | null;
  minimum_amount?: number | string | null;
  free_units?: number | string | null;
  quantity?: number | string | null;
  /**
   * The rate edited on the draft: $ (flat), $/unit or % (pct). Null = the
   * values copied in when the fee was added. Priced as an estimate rate edit
   * (053): the free allowance and the % floor stay.
   */
  rate_override?: number | string | null;
  sort?: number | null;
}

export interface FinalAdjustment {
  id?: string | null;
  description: string;
  /** Signed: + an extra charge, − a discount. */
  amount: number | string;
  taxable: boolean;
  sort?: number | null;
}

export type TipMode = 'none' | 'pct' | 'amount';

/**
 * One estimate line as the draft bills it (062). `removed` drops the line (never
 * the base line). `quantity` replaces the line's units — the recorded units on
 * an access line, before its free allowance. `rate` is the line's own rate: $
 * (flat / custom / base), $/unit, or % — a fee line bills it as an edited rate
 * (053), an access line keeps its percentage floor and free allowance.
 */
export interface FinalLineEdit {
  removed?: boolean | null;
  quantity?: number | string | null;
  rate?: number | string | null;
}

export interface FinalDraft {
  finalWeightLbs?: number | string | null;
  finalHours?: number | string | null;
  finalCrewSize?: number | string | null;
  /** estimate_item id -> rate_snapshot to bill at instead (e.g. DVP repriced). */
  lineRates?: Record<string, number | string> | null;
  /**
   * Estimate lines changed on the draft only (062, invoice_drafts.line_edits):
   * estimate_item id -> removed, or another quantity / rate. Applied after
   * lineRates. The estimate itself is never changed.
   */
  lineEdits?: Record<string, FinalLineEdit> | null;
  adjustments?: FinalAdjustment[] | null;
  tipMode?: TipMode | null;
  tipPct?: number | string | null;
  tipAmount?: number | string | null;
  /**
   * Local hourly (055): the time actually worked, the minimum, and an optional
   * billable override. When travel or on-site is given, finalHours is ignored
   * and the billed hours are finalBilledHours(draft).billedHours.
   */
  finalTravelHours?: number | string | null;
  finalOnsiteHours?: number | string | null;
  finalMinimumHours?: number | string | null;
  finalBillableHours?: number | string | null;
  /** Local hourly (055): trucks on the job. A change from the estimate re-counts Additional Truck. */
  finalTrucks?: number | string | null;
  /** Fees added on the draft (055): priced like estimate fees, after the estimate's lines. */
  fees?: FinalDraftFee[] | null;
}

/** What finalBilledHours worked out. */
export interface FinalTime {
  travelHours: number | null;
  onsiteHours: number | null;
  minimumHours: number | null;
  /** travel + on-site; null when neither was entered. */
  workedHours: number | null;
  /** max(minimum, worked rounded UP to the next 15 min); null without worked hours. */
  calculatedHours: number | null;
  /** What is billed: the override, else the calculated figure, else the legacy finalHours. */
  billedHours: number | null;
  overridden: boolean;
}

/**
 * Billed hours on a local Finalize (055): the override when one is set, else
 * max(minimum, travel + on-site rounded UP to the next 15 min) — the estimate's
 * rule, applied to what the job really took. A draft from before 055 has only
 * finalHours, billed as entered.
 */
export function finalBilledHours(draft: FinalDraft): FinalTime {
  const opt = (v: unknown) => (present(v) ? Math.max(0, num(v)) : null);
  const travel = opt(draft.finalTravelHours);
  const onsite = opt(draft.finalOnsiteHours);
  const minimum = opt(draft.finalMinimumHours);
  const override = present(draft.finalBillableHours) && num(draft.finalBillableHours) > 0 ? num(draft.finalBillableHours) : null;
  const worked = travel != null || onsite != null ? (travel ?? 0) + (onsite ?? 0) : null;
  const calc = worked != null ? Math.max(minimum ?? 0, roundUpToQuarterHour(worked)) : null;
  const calculated = calc != null && calc > 0 ? calc : null;
  const legacy = present(draft.finalHours) && num(draft.finalHours) > 0 ? num(draft.finalHours) : null;
  return {
    travelHours: travel,
    onsiteHours: onsite,
    minimumHours: minimum,
    workedHours: worked,
    calculatedHours: calculated,
    billedHours: override ?? calculated ?? legacy,
    overridden: override != null,
  };
}

/**
 * The company's "Additional Truck" fee (053): active, named "Additional Truck"
 * (any case, "Trucks" too); failing an exact name, the first active fee whose
 * name contains "additional truck". Fees are company-wide, not per tariff.
 */
export function findAdditionalTruckFee<F extends EstimateFee>(fees: F[]): F | undefined {
  const norm = (f: EstimateFee) => String(f?.name ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  const active = fees.filter((f) => f.active !== false);
  return active.find((f) => /^additional trucks?$/.test(norm(f))) ?? active.find((f) => norm(f).includes('additional truck'));
}

/**
 * Trucks changed on Finalize: the Additional Truck line follows, quantity
 * trucks − 1 — re-counted, added at 2+, dropped at 1 — exactly the estimate
 * editor's rule. Unchanged trucks leave the lines as the estimate had them (a
 * line the rep deleted stays deleted). No such fee in the catalog: nothing.
 */
function withFinalTrucks(items: EstimateItemRow[], fees: EstimateFee[], estimateTrucks: number, trucks: number): EstimateItemRow[] {
  if (trucks === estimateTrucks) return items;
  const fee = findAdditionalTruckFee(fees);
  if (!fee) return items;
  const line = items.find((li) => li.source === 'fee' && li.fee_id === fee.id);
  if (trucks >= 2) {
    if (line) return items.map((li) => (li === line ? { ...li, quantity: trucks - 1 } : li));
    const lastSort = items.reduce((m, li) => Math.max(m, num(li.sort_order)), 0);
    return [...items, { id: 'final-additional-truck', source: 'fee', fee_id: fee.id, quantity: trucks - 1, sort_order: lastSort + 1 }];
  }
  return line ? items.filter((li) => li !== line) : items;
}

export interface FinalAdjustmentLine {
  id: string | null;
  description: string;
  amount: number;
  taxable: boolean;
}

export interface CalcFinal extends CalcEstimate {
  /** calcEstimate's total: lines + taxable adjustments, discounted, taxed. */
  taxedTotal: number;
  adjustments: FinalAdjustmentLine[];
  nonTaxableAdjustments: number;
  /** What the client owes before the tip (and before any card fee). */
  totalBeforeTip: number;
  tipBase: number;
  tip: { mode: TipMode; pct: number | null; amount: number };
  finalWeightLbs: number | null;
  /** The billed hours (local): finalTime.billedHours. */
  finalHours: number | null;
  finalCrewSize: number | null;
  /** Local hourly (055): travel, on-site, minimum, calculated, billed, overridden. */
  finalTime: FinalTime;
  finalTrucks: number | null;
}

const cents = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

export function calcFinal(inputs: FinalInputs, draft: FinalDraft): CalcFinal {
  const finalWeight = present(draft.finalWeightLbs) ? num(draft.finalWeightLbs) : null;
  const finalTime = finalBilledHours(draft);
  const finalHours = finalTime.billedHours;
  const finalCrew = present(draft.finalCrewSize) ? num(draft.finalCrewSize) : null;
  const finalTrucks = present(draft.finalTrucks) && num(draft.finalTrucks) >= 1 ? Math.round(num(draft.finalTrucks)) : null;

  const lead: EstimateLead = finalWeight != null ? { ...inputs.lead, weight_lbs: finalWeight } : { ...inputs.lead };

  const rates = draft.lineRates ?? {};
  const edits = draft.lineEdits ?? {};
  let items: EstimateItemRow[] = inputs.items.map((li) => {
    let row: EstimateItemRow = li;
    if (li.id && present(rates[li.id])) row = { ...row, rate_snapshot: num(rates[li.id]) };
    const e = li.id ? edits[li.id] : undefined;
    if (e) row = withLineEdit(row, e);
    if (li.source === 'base') {
      // The billed hours are final — minimum and rounding already applied (or
      // overridden) — so the estimate's minimum must not lift them again.
      if (finalHours != null) row = { ...row, hours: finalHours, minimum_hours: null };
      if (finalCrew != null) row = { ...row, crew_size: finalCrew };
      if (finalTrucks != null) row = { ...row, trucks: finalTrucks };
    }
    return row;
  });
  items = items.filter((li) => !(li.id && li.source !== 'base' && edits[li.id]?.removed));
  // An hourly job finalised with hours but no stored base line still needs one
  // to carry them; its rate falls back to the tariff, as a missing line always has.
  if (finalHours != null && !items.some((li) => li.source === 'base')) {
    items = [{ source: 'base', hours: finalHours, crew_size: finalCrew, trucks: finalTrucks }, ...items];
  }
  if (finalTrucks != null && inputs.tariff?.pricing_method === 'hourly') {
    const estBase = inputs.items.find((li) => li.source === 'base');
    const estTrucks = estBase && present(estBase.trucks) && num(estBase.trucks) >= 1 ? num(estBase.trucks) : 1;
    items = withFinalTrucks(items, inputs.fees, estTrucks, finalTrucks);
  }

  // Fees added on the draft (055): ordinary fee lines priced from their own
  // snapshotted row by calcFeeAmt — a % fee on the FINAL base, as fuel is.
  // After the estimate's lines, before the adjustments.
  const draftFees = (draft.fees ?? [])
    .map((f, i) => ({ f, i }))
    .sort((x, y) => num(x.f.sort ?? x.i) - num(y.f.sort ?? y.i))
    .map(({ f }) => f);
  if (draftFees.length) {
    const feeSort = items.reduce((m, li) => Math.max(m, num(li.sort_order)), 0);
    items = [
      ...items,
      ...draftFees.map((f, i): EstimateItemRow => ({
        id: f.id,
        source: 'fee',
        fee_id: f.fee_id ?? null,
        fee_row: {
          id: f.fee_id ?? (f.access_key ? `access:${f.access_key}` : f.id), name: f.name, fee_type: f.fee_type, amount: f.amount, rate: f.rate, percentage: f.percentage,
          unit_label: f.unit_label, applied_to: f.applied_to, minimum_amount: f.minimum_amount, free_units: f.free_units,
        },
        draft_fee_id: f.id,
        ...(present(f.rate_override) ? { rate_snapshot: num(f.rate_override), rate_overridden: true } : {}),
        quantity: present(f.quantity) ? num(f.quantity) : 1,
        sort_order: feeSort + 500 + i,
      })),
    ];
  }

  const adjustments = (draft.adjustments ?? [])
    .map((a, i) => ({ a, i }))
    .sort((x, y) => num(x.a.sort ?? x.i) - num(y.a.sort ?? y.i))
    .map(({ a }) => a);
  const lastSort = items.reduce((m, li) => Math.max(m, num(li.sort_order)), 0);
  const taxable = adjustments.filter((a) => a.taxable);
  items = [
    ...items,
    ...taxable.map((a, i): EstimateItemRow => ({
      id: a.id ?? null,
      source: 'custom',
      description: a.description,
      rate_snapshot: num(a.amount),
      quantity: 1,
      sort_order: lastSort + 1000 + i,
      adjustment: true,
    })),
  ];

  const est = calcEstimate(lead, items, inputs.tariff, inputs.branch, inputs.fees, inputs.accessorials ?? [], { roundHoursUp: false });

  const nonTaxable = adjustments.filter((a) => !a.taxable).reduce((s, a) => s + num(a.amount), 0);
  const totalBeforeTip = est.total + nonTaxable;
  const tipBase = Math.max(0, est.discountedSubtotal + nonTaxable);

  const mode: TipMode = draft.tipMode === 'pct' || draft.tipMode === 'amount' ? draft.tipMode : 'none';
  const pct = mode === 'pct' ? num(draft.tipPct) : null;
  const tipAmount =
    mode === 'pct' ? cents(tipBase * (num(draft.tipPct) / 100))
    : mode === 'amount' ? cents(Math.max(0, num(draft.tipAmount)))
    : 0;

  return {
    ...est,
    taxedTotal: est.total,
    adjustments: adjustments.map((a) => ({
      id: a.id ?? null, description: a.description, amount: num(a.amount), taxable: !!a.taxable,
    })),
    nonTaxableAdjustments: nonTaxable,
    totalBeforeTip,
    tipBase,
    tip: { mode, pct, amount: tipAmount },
    total: totalBeforeTip + tipAmount,
    finalWeightLbs: finalWeight,
    finalHours,
    finalCrewSize: finalCrew,
    finalTime,
    finalTrucks,
  };
}

/** An estimate line with a draft edit applied (062): quantity and / or rate. */
function withLineEdit(li: EstimateItemRow, e: FinalLineEdit): EstimateItemRow {
  let row: EstimateItemRow = li;
  if (present(e.quantity) && li.source !== 'base') row = { ...row, quantity: num(e.quantity) };
  if (present(e.rate)) {
    const priced = li.source === 'fee' && (li.fee_id || li.auto_key || li.fee_row);
    row = priced
      // A fee or access line bills an edited rate the way an estimate edit does (053).
      ? { ...row, rate_snapshot: num(e.rate), rate_overridden: true, ...(li.auto_key ? { auto_edited: true } : {}) }
      : { ...row, rate_snapshot: num(e.rate) };
  }
  return row;
}

/** An estimate line the draft changed, as the Final breakdown and the invoice record show it. */
export interface FinalLineChange {
  itemId: string;
  desc: string;
  /** The line on the estimate as quoted. */
  from: number;
  /** The line on the draft; null = removed. */
  to: number | null;
}

/**
 * The estimate lines a draft changed (062): each edited line, its amount on the
 * estimate as quoted, and on the draft (null when removed).
 */
export function finalLineChanges(inputs: FinalInputs, edits: Record<string, FinalLineEdit> | null | undefined, final: CalcEstimate): FinalLineChange[] {
  const ids = Object.keys(edits ?? {});
  if (!ids.length) return [];
  const quote = calcEstimate(inputs.lead, inputs.items, inputs.tariff, inputs.branch, inputs.fees, inputs.accessorials ?? []);
  const out: FinalLineChange[] = [];
  for (const l of quote.items) {
    if (!l.id || !ids.includes(l.id)) continue;
    const e = (edits ?? {})[l.id];
    if (!e || (!e.removed && !present(e.quantity) && !present(e.rate))) continue;
    const now = final.items.find((x) => x.id === l.id);
    out.push({ itemId: l.id, desc: l.desc, from: cents(l.net), to: now ? cents(now.net) : null });
  }
  return out;
}

// ============================================================================
// CONTRACTOR ESTIMATE (Phase 8f.1)
// ----------------------------------------------------------------------------
// Same fee mechanics as the customer estimate — it reuses calcRateLine and
// calcFeeAmt so the per-line arithmetic never diverges — but B2B: NO tax, NO
// discounts. Each line carries its own computable fields (the broker edits
// them directly); the base line reads the lead's LIVE weight (weight method)
// so a weight change recomputes the line. total = Σ(line nets).
// ============================================================================

export interface ContractorItemRow {
  id?: string | null;
  fee_id?: string | null;
  description?: string | null;
  source?: LineSource | null;
  pricing_method?: string | null; // base line: 'weight' | 'hourly' | 'cuft' | 'flat'
  fee_type?: 'flat' | 'pct' | 'unit' | null;
  amount?: number | string | null; // flat fee $ (broker-editable)
  rate?: number | string | null; // base $/unit, unit-fee $/unit, custom $/unit
  percentage?: number | string | null; // pct fee %
  quantity?: number | string | null; // hours / units (weight base uses live lead.weight_lbs)
  unit_label?: string | null;
  applied_to?: 'base' | 'subtotal' | null;
  sort_order?: number | string | null;
  /** A1: '{leg}:{accessorial}' when this line was auto-added; null if a human typed it. */
  auto_key?: string | null;
  /** A1: true once a rep changed the line — from then on nothing recomputes it. */
  auto_edited?: boolean | null;
}

export interface ContractorCalcLine {
  id: string | null;
  desc: string;
  source: LineSource;
  feeId: string | null;
  feeType: 'flat' | 'pct' | 'unit' | null;
  pricingMethod: string | null;
  /** The editable field the UI should surface: rate ($/x), percentage (%), or amount ($). */
  rateKind: 'weight' | 'hourly' | 'unit' | 'pct' | 'flat' | 'money';
  rate: number;
  percentage: number;
  amount: number;
  quantity: number;
  unitLabel: string | null;
  /** Computed contractor pay for this line (no discount, no tax). */
  net: number;
  /** A1 — mirrors the client CalcLine so both pay tables state rules identically. */
  autoKey: string | null;
  minimumAmount: number;
  freeUnits: number;
  rawQuantity: number;
  /** Hourly base line priced from the invoice: the crew, for the label (049). */
  crewSize?: number | null;
}

export interface ContractorCalc {
  items: ContractorCalcLine[];
  total: number;
}

/** Contractor-pay floored weight (MW2). Independent of the customer floor: uses
 * contractor_minimum_weight (which mirrors the client minimum until overridden).
 * Compute/display only — never writes the shared weight_lbs, so a differing
 * contractor floor can't inflate the customer estimate. */
export function contractorBillableWeight(lead: EstimateLead): number {
  return Math.max(num(lead.weight_lbs), num(lead.contractor_minimum_weight));
}

/**
 * Finalize phase 2 (049): the issued invoice's final figures, stored on the
 * contractor estimate row (final_weight_lbs / final_hours / final_crew_size).
 * Absent = today's behaviour: the live lead weight, the line's stored hours.
 */
export interface ContractorFinal {
  weightLbs?: number | string | null;
  hours?: number | string | null;
  crewSize?: number | string | null;
}

/** The final figures on a contractor_estimates row, or null when it has none. */
export function contractorFinalOf(ce: any): ContractorFinal | null {
  if (!ce) return null;
  const f = { weightLbs: ce.final_weight_lbs ?? null, hours: ce.final_hours ?? null, crewSize: ce.final_crew_size ?? null };
  return present(f.weightLbs) || present(f.hours) ? f : null;
}

/**
 * Base-line quantity: weight/cuft read the lead LIVE (weight: the invoice's final
 * weight once issued); hourly/flat use the stored qty (hourly: the final hours).
 */
function contractorBaseQty(method: string | null | undefined, lead: EstimateLead, stored: unknown, final: ContractorFinal | null): number {
  // weight: the final weight replaces the live one; the contractor minimum still floors it.
  if (method === 'weight') {
    return contractorBillableWeight(final && present(final.weightLbs) ? { ...lead, weight_lbs: num(final.weightLbs) } : lead);
  }
  if (method === 'cuft') return num(lead.cubic_feet);
  // hourly: the final hours replace the stored quantity. Crew is display only —
  // the rate is per crew-hour, as on the client side.
  if (method === 'hourly' && final && present(final.hours)) return num(final.hours);
  return stored != null && stored !== '' ? num(stored) : 1;
}

/**
 * Contractor estimate: mirrors calcEstimate's line math (via calcRateLine /
 * calcFeeAmt) but skips discounts and tax. The base line (if present) is
 * computed first so percentage fees applied to 'base' see its gross.
 */
export function calcContractorEstimate(
  items: ContractorItemRow[],
  lead: EstimateLead,
  // A1: the tariff's CONTRACTOR accessorial rates — its own set, its own values.
  accessorials: AccessorialRate[] = [],
  // 049: the issued invoice's final figures (contractorFinalOf(row)); null = live.
  final: ContractorFinal | null = null
): ContractorCalc {
  const out: ContractorCalcLine[] = [];

  const base = items.find((li) => li.source === 'base');
  let baseGross = 0;
  let subtotalSoFar = 0;

  if (base) {
    const method = base.pricing_method ?? null;
    const rate = num(base.rate);
    const qty = contractorBaseQty(method, lead, base.quantity, final);
    const gross = calcRateLine(rate, qty);
    out.push({
      id: base.id ?? null,
      desc: base.description || 'Base charge',
      source: 'base',
      feeId: null,
      feeType: null,
      pricingMethod: method,
      rateKind: method === 'weight' ? 'weight' : method === 'hourly' ? 'hourly' : method === 'cuft' ? 'weight' : 'money',
      rate,
      percentage: 0,
      amount: 0,
      quantity: qty,
      unitLabel: method === 'weight' ? 'lb' : method === 'cuft' ? 'cuft' : method === 'hourly' ? 'hr' : null,
      net: gross,
      autoKey: null,
      minimumAmount: 0,
      freeUnits: 0,
      rawQuantity: qty,
      crewSize: method === 'hourly' && final && present(final.crewSize) ? num(final.crewSize) : null,
    });
    baseGross = gross;
    subtotalSoFar = gross;
  }

  const others = items
    .filter((li) => li.source !== 'base')
    .sort((a, b) => num(a.sort_order) - num(b.sort_order));

  for (const li of others) {
    const qty = li.quantity != null && li.quantity !== '' ? num(li.quantity) : 1;
    let gross: number;
    let rate = 0;
    let percentage = 0;
    let amount = 0;
    let rateKind: ContractorCalcLine['rateKind'] = 'money';
    let unitLabel: string | null = null;

    // A1 — an AUTO-ADDED accessorial line prices from its CONTRACTOR rate row.
    // Once a broker edits it (auto_edited), it is theirs and nothing recomputes it.
    const autoRate =
      li.auto_key && !li.auto_edited
        ? accessorials.find((r) => accessorialKey(r.leg, r.accessorial) === li.auto_key && r.active !== false)
        : undefined;

    // Declared in the loop scope (not inside the branch) because the push below
    // needs it to state the free allowance and the percentage floor.
    let feeLike: EstimateFee | null = null;

    if (autoRate || li.fee_type) {
      // Reuse the customer fee arithmetic with the item's own (broker-edited) params.
      feeLike = autoRate
        ? feeFromAccessorial(autoRate)
        : {
            id: li.fee_id ?? '',
            fee_type: li.fee_type,
            amount: li.amount,
            rate: li.rate,
            percentage: li.percentage,
            applied_to: li.applied_to ?? 'base',
          };
      // The contractor engine applies NO discounts, so baseGross is already the
      // net transportation charge — same rule as the client side, same value.
      gross = calcFeeAmt(feeLike, baseGross, subtotalSoFar, qty);
      // Read from feeLike, not li — an auto accessorial line carries its type on
      // the RATE ROW, not on the stored item.
      if (feeLike.fee_type === 'unit') {
        rate = num(feeLike.rate);
        rateKind = 'unit';
        unitLabel = feeLike.unit_label ?? li.unit_label ?? null;
      } else if (feeLike.fee_type === 'pct') {
        percentage = num(feeLike.percentage);
        rateKind = 'pct';
      } else {
        amount = num(feeLike.amount);
        rateKind = 'flat';
      }
    } else {
      // Custom line: rate × qty (fall back to the amount column).
      rate = li.rate != null && li.rate !== '' ? num(li.rate) : num(li.amount);
      gross = calcRateLine(rate, qty);
      rateKind = 'money';
    }

    out.push({
      id: li.id ?? null,
      desc: autoRate ? (autoRate.label || li.description || 'Accessorial') : li.description || 'Custom line',
      source: (li.source as LineSource) ?? 'fee',
      feeId: li.fee_id ?? null,
      feeType: feeLike?.fee_type ?? li.fee_type ?? null,
      pricingMethod: null,
      rateKind,
      rate,
      percentage,
      amount,
      quantity: feeLike && feeLike.fee_type === 'unit' ? chargeableUnits(feeLike, qty) : qty,
      unitLabel,
      net: gross,
      autoKey: li.auto_key ?? null,
      minimumAmount: feeLike ? num(feeLike.minimum_amount) : 0,
      freeUnits: feeLike ? num(feeLike.free_units) : 0,
      rawQuantity: qty,
    });
    subtotalSoFar += gross;
  }

  const total = out.reduce((s, l) => s + l.net, 0);
  return { items: out, total };
}
